import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { reduxForm, change, untouch, formValues, submit } from 'redux-form'
import { RadioButton, RadioButtonGroup } from '@ynap/radio-button'
import Field from '@ynap/field'
import BEMHelper from '@ynap/bem-helper'
import { Button } from '@ynap/button'
import validate from './validate'
import { CombinedSelect as Select } from '@ynap/select'
import { SizeSelect, ColoursWithState } from '@ynap/product-details-page'
import { ToggleSection } from '@ynap/toggle'
import track from 'react-tracking'
import { trackCreateReturnExchangeItem } from './analytics'
import { filterReasonOptions } from './newReturnReasonsUtils'

const bem = new BEMHelper('ReturnsAndExchangesForm')

const propTypes = {
    form: PropTypes.string.isRequired,
    error: PropTypes.string,
    submitting: PropTypes.bool,
    handleSubmit: PropTypes.func,
    className: PropTypes.string,
    messages: PropTypes.shape({
        returnsAndExchangesForm: PropTypes.shape({
            title: PropTypes.func.isRequired,
            exchangeLabel: PropTypes.func.isRequired,
            returnLabel: PropTypes.func.isRequired,
            reasonLabel: PropTypes.func.isRequired,
            reasonPlaceholder: PropTypes.func.isRequired,
            sizesAndColoursTitle: PropTypes.func.isRequired,
            colourLabel: PropTypes.func.isRequired,
            sizePlaceholder: PropTypes.func.isRequired,
            selectReasonReturn: PropTypes.func.isRequired,
            selectReasonExchange: PropTypes.func.isRequired,
        }),
    }),
    returnCodes: PropTypes.array.isRequired,
    colours: PropTypes.array.isRequired,
    skus: PropTypes.array.isRequired,
    returnOrExchange: PropTypes.string.isRequired,
    selectedProductColour: PropTypes.string.isRequired,
    selectProductColour: PropTypes.func.isRequired,
    selectReturnCode: PropTypes.func.isRequired,
    selectedReturnCode: PropTypes.string.isRequired,
    selectReturnOrExchange: PropTypes.func.isRequired,
    lineNumber: PropTypes.string.isRequired,
    showCommentBox: PropTypes.bool,
    soldOut: PropTypes.object,
    sizeGridFeatureToggle: PropTypes.bool,
    invalid: PropTypes.bool,
    coloursWithSkus: PropTypes.array.isRequired,
}

const defaultProps = {
    submitting: false,
    component: 'form',
    form: 'returnsAndExchangesForm',
}

@track({
    category: {
        subCategory: 'my orders',
    },
})
export class ReturnsAndExchangesForm extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            shuffledFilteredReasonOptions: [],
            hasShuffled: false,
        }
    }

    componentDidMount() {
        const { initialReturnValues, selectReturnOrExchange, selectReturnCode, dispatch, selectProductColour } =
            this.props

        if (initialReturnValues) {
            const { returnOrExchange, reasonCode, selectedProductColour, exchangePartnumber, lineNumber } =
                initialReturnValues

            if (returnOrExchange === 'exchange') {
                selectReturnOrExchange(returnOrExchange, lineNumber)
                selectReturnCode(reasonCode, lineNumber)
                dispatch(change('returnsAndExchangesForm', 'reasonCode', reasonCode))
                selectProductColour(selectedProductColour, lineNumber)
                dispatch(change('returnsAndExchangesForm', 'exchangePartnumber', exchangePartnumber))
            }

            if (returnOrExchange === 'return') {
                selectReturnOrExchange(returnOrExchange, lineNumber)
                selectReturnCode(reasonCode, lineNumber)
                dispatch(change('returnsAndExchangesForm', 'reasonCode', reasonCode))
            }
        }
    }

    componentDidUpdate(prevProps) {
        const {
            skus,
            dispatch,
            selectedProductColour,
            reasonCode,
            colours,
            _reduxForm,
            lineNumber,
            selectProductColour,
        } = this.props

        if (!skus) return

        /**
         * Conditional used to avoid dispatching actions when exchangePartnumber has just been changed
         */
        if (
            _reduxForm.values.exchangePartnumber &&
            prevProps._reduxForm.values.exchangePartnumber !== _reduxForm.values.exchangePartnumber
        )
            return

        /**
         * When there are only one SKU but more than one colour, these actions need to be dispatched
         * in order to set the default colour as selectProductColour in redux-form and order-history-state
         */
        if (skus.length === 1 && colours.length > 1 && prevProps.reasonCode !== reasonCode && selectedProductColour) {
            selectProductColour(selectedProductColour, lineNumber)
            dispatch(
                change(
                    'returnsAndExchangesForm',
                    'exchangePartnumber',
                    this.getUsableSkuFromVariant(selectedProductColour)
                )
            )
            return
        }

        /**
         * When colour or reasonCode is changed (in multiple-size products), or a reasonCode is changed in products with
         * just one colour, these actions need to be dispatched to select the available SKU
         */
        if (
            (selectedProductColour &&
                (prevProps.selectedProductColour !== selectedProductColour || prevProps.reasonCode !== reasonCode)) ||
            (colours.length <= 1 && !selectedProductColour && prevProps.reasonCode !== reasonCode)
        ) {
            const filteredSkus = this.getUsableSkus()
            if (filteredSkus.length === 1) {
                dispatch(change('returnsAndExchangesForm', 'exchangePartnumber', filteredSkus[0].partNumber))
            }
        }
    }

    onSelectReturnOrExchange(returnOrExchange) {
        const { selectReturnOrExchange, lineNumber, selectReturnCode, selectProductColour, dispatch } = this.props
        selectReturnOrExchange(returnOrExchange, lineNumber)
        selectReturnCode(null, lineNumber)
        selectProductColour(null, lineNumber)
        dispatch(change('returnsAndExchangesForm', 'reasonCode', null))
        dispatch(untouch('returnsAndExchangesForm', 'reasonCode'))
        dispatch(change('returnsAndExchangesForm', 'exchangePartnumber', null))
    }

    onSelectReasonCode(reasonCode) {
        const { lineNumber, selectReturnCode, selectProductColour, dispatch } = this.props

        selectReturnCode(reasonCode, lineNumber)
        selectProductColour(null, lineNumber)
        dispatch(change('returnsAndExchangesForm', 'reasonCode', reasonCode))
        dispatch(change('returnsAndExchangesForm', 'exchangePartnumber', null))
    }

    onSelectProductColour(variantNumber) {
        const {
            lineNumber,
            selectProductColour,
            dispatch,
            item: { skus = [] },
        } = this.props
        const value = skus.length > 1 ? null : this.getUsableSkuFromVariant(variantNumber)
        selectProductColour(variantNumber, lineNumber)
        dispatch(change('returnsAndExchangesForm', 'exchangePartnumber', value))
    }

    refundProduct() {
        const { dispatch, selectedReturnCode, addReturn, externalOrderId, item, urlPath } = this.props
        addReturn(
            externalOrderId,
            { ...item, returnOrExchange: 'return' },
            { reasonCode: selectedReturnCode, exchangePartnumber: null },
            urlPath
        )
    }

    // @todo the `filter` line here is a fix for a backend issue where it may return some partnumbers
    // which do not match any skus. This has been referred to Team 4 for a backend fix.
    getUsableSkus() {
        const { returnCodes, reasonCode, skus, coloursWithSkus, selectedProductColour } = this.props

        // in the case where we change colors we need to find the respective skus and then check for the correct partnumber to be sent
        const skusForColor = coloursWithSkus?.find((color) => color.partNumber === selectedProductColour)?.sKUs
        const skusToUse = skusForColor ?? skus
        return (
            returnCodes
                ?.find(({ code }) => code === reasonCode)
                ?.availableExchanges?.map((availableExchange) =>
                    skusToUse.find(({ partNumber }) => partNumber === availableExchange)
                )
                .filter((item) => item) || []
        )
    }

    getUsableSkuFromVariant(variantNumber) {
        const { coloursWithSkus } = this.props
        const colour = coloursWithSkus.find((colour) => colour.partNumber === variantNumber)
        return colour?.sKUs[0]?.partNumber
    }

    getLabelFromVariant() {
        const { coloursWithSkus, selectedProductColour } = this.props
        return coloursWithSkus.find((colour) => colour.partNumber === selectedProductColour)?.label
    }

    @track(({ item, externalOrderId, locale }) => trackCreateReturnExchangeItem(item, externalOrderId, locale))
    handleContinueExchangeClick = () => {}

    shuffleArray(array) {
        const newArray = [...array]
        const length = newArray.length

        for (let start = 0; start < length; start++) {
            const randomPosition = Math.floor((newArray.length - start) * Math.random())
            const randomItem = newArray.splice(randomPosition, 1)

            newArray.push(...randomItem)
        }
        this.setState({ hasShuffled: true })
        return newArray
    }

    render() {
        const {
            component: Component,
            form,
            messages,
            submitting,
            className,
            handleSubmit,
            returnCodes,
            invalid,
            colours,
            skus,
            showCommentBox,
            returnOrExchange,
            exchangable,
            soldOut,
            reasonCode,
            sizeGridFeatureToggle,
        } = this.props

        let returnCodesAvailable = returnCodes
        if (returnOrExchange === 'exchange') {
            returnCodesAvailable = returnCodes.reduce((accumulator, value) => {
                const sKUsPartNumbers = (skus && skus.map((element) => element.partNumber)) || []
                const intersection = value.availableExchanges?.filter((value) => -1 !== sKUsPartNumbers.indexOf(value))
                return value.availableExchanges?.length >= 1 && intersection.length > 0
                    ? [...accumulator, value]
                    : [...accumulator]
            }, [])
        } else {
            returnCodesAvailable = returnCodes
        }

        const reasonOptions = returnCodesAvailable.map((reason) => ({
            key: reason.code,
            value: reason.code,
            label: reason.description,
            labelNative: reason.description,
        }))

        const filteredReasonOptions = filterReasonOptions(returnOrExchange, reasonOptions)

        !this.state.hasShuffled &&
            returnOrExchange === 'return' &&
            this.setState({
                shuffledFilteredReasonOptions: this.shuffleArray(filteredReasonOptions),
            })

        return (
            <Component id={form} className={bem(null, { submitting }, className)} onSubmit={handleSubmit}>
                {!soldOut ? (
                    <RadioButtonGroup
                        value={returnOrExchange}
                        id="returnOrExchange"
                        name="returnOrExchange"
                        onChange={(e) => this.onSelectReturnOrExchange(e.target.value)}
                    >
                        {exchangable ? (
                            <RadioButton value="exchange" label={messages.returnsAndExchangesForm.exchangeLabel()} />
                        ) : null}
                        <RadioButton value="return" label={messages.returnsAndExchangesForm.returnLabel()} />
                    </RadioButtonGroup>
                ) : null}

                {returnOrExchange === 'return' && messages.returnsAndExchangesForm.returnInfoNotif ? (
                    <p className={bem('returnMessage')}>
                        {messages.returnsAndExchangesForm.returnInfoNotif()}
                    </p>
                ) : null}

                {returnCodesAvailable && returnCodesAvailable.length > 0 && (
                    <Field
                        component={Select}
                        name="reasonCode"
                        className={bem('reasonCode')}
                        label={messages.returnsAndExchangesForm.reasonLabel()}
                        emptyLabel={
                            returnOrExchange === 'return'
                                ? messages.returnsAndExchangesForm.selectReasonReturn()
                                : messages.returnsAndExchangesForm.selectReasonExchange()
                        }
                        onChange={(e) => {
                            this.onSelectReasonCode(e)
                        }}
                        selected={!!reasonCode}
                        options={
                            returnOrExchange === 'return'
                                ? this.state.shuffledFilteredReasonOptions
                                : filteredReasonOptions
                        }
                        numOfListElementsDisplayed={5}
                    />
                )}

                {showCommentBox ? <Field component="textarea" className={bem('returnNote')} name="returnNote" /> : null}

                {returnOrExchange === 'exchange' && reasonCode && (
                    <ToggleSection
                        className={bem('sizesAndColoursTitle')}
                        title={messages.returnsAndExchangesForm.sizesAndColoursTitle()}
                        defaultOpen
                    >
                        <div className={bem('sizesAndColurs')}>
                            <div className={bem('colours')}>
                                <ColoursWithState
                                    messages={messages}
                                    colours={colours}
                                    colourLabel={this.getLabelFromVariant()}
                                    selectProduct={(productColour) =>
                                        this.onSelectProductColour(productColour.partNumber)
                                    }
                                    collapseColours={false}
                                />
                            </div>
                            <div className={bem('sizes')}>
                                <Field
                                    name={'exchangePartnumber'}
                                    component={SizeSelect}
                                    customValue={'partNumber'}
                                    messages={messages}
                                    displaySizeGuide={false}
                                    displayLabel={false}
                                    skus={this.getUsableSkus()}
                                    className={bem('exchangePartNumber')}
                                    sizeGridFeatureToggle={sizeGridFeatureToggle}
                                />
                            </div>
                        </div>
                    </ToggleSection>
                )}

                <Button
                    type="primary"
                    className={bem('submit', submitting || invalid ? 'disabled' : null)}
                    disabled={submitting || invalid}
                    onClick={this.handleContinueExchangeClick}
                >
                    {messages.returnsAndExchangesForm.submit()}
                </Button>
                {returnOrExchange === 'exchange' && reasonCode && (
                    <Button type="primary-link" className={bem('refund')} onClick={() => this.refundProduct()}>
                        {messages.returnsAndExchangesForm.refundLabel()}
                    </Button>
                )}
            </Component>
        )
    }
}

ReturnsAndExchangesForm.propTypes = propTypes
ReturnsAndExchangesForm.defaultProps = defaultProps
const formWithValues = formValues('reasonCode', 'exchangePartnumber')(ReturnsAndExchangesForm)
export default reduxForm({ form: 'returnsAndExchangesForm', validate })(formWithValues)
